/**
 * Role Management Service
 * 
 * This service handles user role management operations for the multi-role system
 */

import { unifiedApi } from './UnifiedApiService';
import { UserRole } from '../types/user';
import { Church } from '../types/church';

// Import User type from the context since it has the extended version with roles
type User = {
  id: string;
  email: string;
  name: string;
  role: string;
  roles?: UserRole[];
  primaryRole?: { role_type: string; church?: { id: string; name: string; } };
  church?: string;
  church_id?: string;
  churchId?: string;
};

export interface AssignRoleRequest {
  role_type: UserRole['role_type'];
  church_id?: string;
  ministry_ids?: string[];
  group_ids?: string[];
  service_ids?: string[];
  is_primary?: boolean;
  expires_date?: string;
  jurisdiction?: string;
  district?: string;
  custom_permissions?: string[];
}

export interface RoleTypeOption {
  value: UserRole['role_type'];
  label: string;
  description: string;
  requires_church: boolean;
  allows_ministries: boolean;
  allows_groups: boolean;
  allows_services: boolean;
  is_denomination_level: boolean;
}

class RoleService {
  /**
   * Get all roles for a user
   */
  async getUserRoles(userId: string): Promise<UserRole[]> {
    try {
      const response = await unifiedApi.get<UserRole[]>(`/auth/users/${userId}/roles/`);
      return response;
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
      throw error;
    }
  }

  /**
   * Assign a new role to a user
   */
  async assignRole(userId: string, roleData: AssignRoleRequest): Promise<UserRole> {
    try {
      return await unifiedApi.post<UserRole>(`/auth/users/${userId}/roles/`, roleData);
    } catch (error) {
      console.error('Failed to assign role:', error);
      throw error;
    }
  }

  /**
   * Remove a role from a user
   */
  async removeRole(userId: string, roleId: string): Promise<void> {
    try {
      await unifiedApi.delete(`/auth/users/${userId}/roles/${roleId}/`);
    } catch (error) {
      console.error('Failed to remove role:', error);
      throw error;
    }
  }

  /**
   * Update a user's role
   */
  async updateRole(userId: string, roleId: string, updates: Partial<AssignRoleRequest>): Promise<UserRole> {
    try {
      return await unifiedApi.patch<UserRole>(`/auth/users/${userId}/roles/${roleId}/`, updates);
    } catch (error) {
      console.error('Failed to update role:', error);
      throw error;
    }
  }

  /**
   * Set a role as primary
   */
  async setPrimaryRole(userId: string, roleId: string): Promise<UserRole> {
    try {
      return await unifiedApi.post<UserRole>(`/auth/users/${userId}/roles/${roleId}/set_primary/`, {});
    } catch (error) {
      console.error('Failed to set primary role:', error);
      throw error;
    }
  }

  /**
   * Get available role types with descriptions
   */
  getRoleTypes(): RoleTypeOption[] {
    return [
      {
        value: 'SUPERADMIN',
        label: 'Super Administrator',
        description: 'Platform-wide administrative access',
        requires_church: false,
        allows_ministries: false,
        allows_groups: false,
        allows_services: false,
        is_denomination_level: false
      },
      {
        value: 'ADMIN',
        label: 'Church Administrator',
        description: 'Full administrative access to a specific church',
        requires_church: true,
        allows_ministries: false,
        allows_groups: false,
        allows_services: false,
        is_denomination_level: false
      },
      {
        value: 'PASTOR',
        label: 'Pastor',
        description: 'Pastoral role with full church access and platform-wide learning resources',
        requires_church: true,
        allows_ministries: false,
        allows_groups: false,
        allows_services: false,
        is_denomination_level: false
      },
      {
        value: 'MINISTER',
        label: 'Minister',
        description: 'Head of one or more ministries',
        requires_church: true,
        allows_ministries: true,
        allows_groups: false,
        allows_services: false,
        is_denomination_level: false
      },
      {
        value: 'LEADER',
        label: 'Group/Service Leader',
        description: 'Leader of specific groups or services',
        requires_church: true,
        allows_ministries: false,
        allows_groups: true,
        allows_services: true,
        is_denomination_level: false
      },
      {
        value: 'BISHOP',
        label: 'Bishop',
        description: 'Denominational oversight at jurisdiction level',
        requires_church: false,
        allows_ministries: false,
        allows_groups: false,
        allows_services: false,
        is_denomination_level: true
      },
      {
        value: 'SUPERINTENDENT',
        label: 'Superintendent',
        description: 'Denominational oversight at district level',
        requires_church: false,
        allows_ministries: false,
        allows_groups: false,
        allows_services: false,
        is_denomination_level: true
      }
    ];
  }

  /**
   * Get churches accessible to a user based on their roles
   */
  async getAccessibleChurches(userId: string): Promise<Church[]> {
    try {
      const response = await unifiedApi.get<Church[]>(`/auth/users/${userId}/accessible-churches/`);
      return response;
    } catch (error) {
      console.error('Failed to fetch accessible churches:', error);
      throw error;
    }
  }

  /**
   * Check if user has a specific role type
   */
  hasRole(user: User | null, roleType: UserRole['role_type']): boolean {
    if (!user || !user.roles) return false;
    return user.roles.some(role => role.role_type === roleType && role.is_active);
  }

  /**
   * Check if user has any of the specified role types
   */
  hasAnyRole(user: User | null, roleTypes: UserRole['role_type'][]): boolean {
    if (!user || !user.roles) return false;
    return user.roles.some(role => 
      roleTypes.includes(role.role_type) && role.is_active
    );
  }

  /**
   * Get user's highest role (based on hierarchy)
   */
  getHighestRole(user: User | null): UserRole | null {
    if (!user || !user.roles || user.roles.length === 0) return null;

    const hierarchy = [
      'SUPERADMIN',
      'BISHOP',
      'SUPERINTENDENT', 
      'ADMIN',
      'PASTOR',
      'MINISTER',
      'LEADER'
    ];

    // Sort roles by hierarchy
    const sortedRoles = [...user.roles]
      .filter(role => role.is_active)
      .sort((a, b) => {
        const aIndex = hierarchy.indexOf(a.role_type);
        const bIndex = hierarchy.indexOf(b.role_type);
        return aIndex - bIndex;
      });

    return sortedRoles[0] || null;
  }

  /**
   * Get user's primary role
   */
  getPrimaryRole(user: User | null): UserRole | null {
    if (!user || !user.roles || user.roles.length === 0) return null;
    
    // First check if there's a role marked as primary
    const primaryRole = user.roles.find(role => role.is_primary && role.is_active);
    if (primaryRole) return primaryRole;
    
    // Otherwise return the highest role
    return this.getHighestRole(user);
  }

  /**
   * Format role for display
   */
  formatRoleDisplay(role: UserRole): string {
    const roleType = this.getRoleTypes().find(rt => rt.value === role.role_type);
    let display = roleType?.label || role.role_type;
    
    if (role.church) {
      display += ` - ${role.church.name}`;
    }
    
    if (role.jurisdiction) {
      display += ` (Jurisdiction: ${role.jurisdiction})`;
    } else if (role.district) {
      display += ` (District: ${role.district})`;
    }
    
    return display;
  }

  /**
   * Check if role is expired
   */
  isRoleExpired(role: UserRole): boolean {
    if (!role.expires_date) return false;
    return new Date(role.expires_date) < new Date();
  }

  /**
   * Get role badge color/variant
   */
  getRoleBadgeVariant(roleType: UserRole['role_type']): string {
    const variants: Record<UserRole['role_type'], string> = {
      'SUPERADMIN': 'danger',
      'ADMIN': 'warning',
      'PASTOR': 'info',
      'MINISTER': 'primary',
      'LEADER': 'success',
      'BISHOP': 'secondary',
      'SUPERINTENDENT': 'secondary'
    };
    return variants[roleType] || 'default';
  }

  /**
   * Validate church access based on user roles
   * Frontend validation layer for additional security
   */
  validateChurchAccess(churches: Church[], user: User): Church[] {
    if (!user.roles || user.roles.length === 0) {
      // Fallback to old system - if user has a church, they can access it
      if (user.church || user.church_id) {
        return churches.filter(c => 
          c.id === user.church || c.id === user.church_id
        );
      }
      return churches;
    }

    // SUPERADMIN sees all churches
    if (this.hasRole(user, 'SUPERADMIN')) {
      return churches;
    }

    // Filter churches based on user roles
    return churches.filter(church => {
      // Check each role for access to this church
      return user.roles!.some(role => {
        // Direct church assignment
        if (role.church?.id === church.id) {
          return true;
        }

        // BISHOP - jurisdiction match
        if (role.role_type === 'BISHOP' && role.jurisdiction) {
          // Check if church is in the bishop's jurisdiction
          // This would need church to have jurisdiction field
          return (church as any).jurisdiction === role.jurisdiction;
        }

        // SUPERINTENDENT - district match
        if (role.role_type === 'SUPERINTENDENT' && role.district) {
          // Check if church is in the superintendent's district
          // This would need church to have district field
          return (church as any).district === role.district;
        }

        return false;
      });
    });
  }

  /**
   * Check if user has access to a specific church
   */
  hasChurchAccess(user: User, churchId: string): boolean {
    if (!user.roles || user.roles.length === 0) {
      // Fallback to old system
      return user.church === churchId || user.church_id === churchId;
    }

    // SUPERADMIN has access to all
    if (this.hasRole(user, 'SUPERADMIN')) {
      return true;
    }

    // Check roles for access
    return user.roles.some(role => {
      if (role.church?.id === churchId) {
        return true;
      }
      // For denomination roles, would need to check jurisdiction/district
      return false;
    });
  }
}

// Create and export a singleton instance
export const roleService = new RoleService();